import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ErrorNote, Icon } from 'venn-ui-kit';
import { createDefaultLot, type LotInput } from './types';
import { hasInconsistentLotPricing } from './utils';
import { getLotIncompleteFields, TaxLotInputRow } from './TaxLotInputRow';

export const TaxInfoStep = ({
  lots,
  onLotsChange,
  setSaveDisabled,
}: {
  lots: LotInput[];
  onLotsChange: (lots: LotInput[]) => void;
  setSaveDisabled: (isSaveDisabled: boolean) => void;
}) => {
  const handleLotChange = useCallback(
    (index: number, updated: LotInput) => {
      const newLots = [...lots];
      newLots[index] = { ...updated, isNew: false };
      onLotsChange(newLots);
    },
    [lots, onLotsChange],
  );

  const handleRemoveLot = useCallback(
    (id: string) => {
      onLotsChange(lots.filter((lot) => lot.id !== id));
    },
    [lots, onLotsChange],
  );

  const handleAddLot = useCallback(() => {
    onLotsChange([...lots, createDefaultLot()]);
  }, [lots, onLotsChange]);

  const costPerShareInconsistent = useMemo(() => {
    const isInconsistent = hasInconsistentLotPricing({ lots });
    setSaveDisabled(isInconsistent);
    return isInconsistent;
  }, [lots, setSaveDisabled]);
  const [noteDismissed, setNoteDismissed] = useState(false);

  useEffect(() => {
    if (!costPerShareInconsistent) setNoteDismissed(false);
  }, [costPerShareInconsistent]);

  const showNote = costPerShareInconsistent && !noteDismissed;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {showNote && (
        <div className="flex justify-end pr-[66px]">
          <ErrorNote size="small">
            Current Price must be consistent across all lots.
            <button
              type="button"
              onClick={() => setNoteDismissed(true)}
              className="border-0 bg-transparent cursor-pointer p-0 flex items-center [&_i]:text-[12px]"
            >
              <Icon type="xmark" />
            </button>
          </ErrorNote>
        </div>
      )}
      <div className="flex flex-col flex-1 overflow-y-auto p-[16px] gap-[16px]">
        {lots.map((lot, index) => (
          <TaxLotInputRow
            key={lot.id}
            lot={lot}
            onChange={(updated) => handleLotChange(index, updated)}
            onRemove={() => handleRemoveLot(lot.id)}
            index={index}
            incompleteFields={getLotIncompleteFields(lot)}
            costPerShareInconsistent={costPerShareInconsistent}
          />
        ))}

        <div>
          <Button refreshedStyling noMargin icon="plus" onClick={handleAddLot} data-testid="qa-add-lot">
            Additional Lot
          </Button>
        </div>
      </div>
    </div>
  );
};
